/**
 * personelBelgeService.js — kişi belgelerinin üç yeni yüzü (backlog #142, #145, #250).
 *
 *   1. PAYLAŞIMLI BELGELER (#142): SGK Hizmet Dökümü (96) ve İSG Eğitim Katılım Listesi (98) firmanın
 *      belgesidir, firma onu birçok çalışanına dağıtır. İnceleme çalışan BAŞINA yapılır.
 *      /personnel/firma/{firmaId}/paylasimli-belgeler (rehber §8).
 *   2. BELGE TAŞIMA (#145): yanlış yüklenen belgeyi başka kategoriye / aynı firmadaki başka kişiye taşır.
 *      /personnel/{personId}/dosya/{fileId}/tasi (rehber §9).
 *   3. KİŞİ HAVUZU (#250): türü henüz belli olmayan dosyalar (file_type 92 + kişi bağı). Rehber §9.
 *
 * Hepsi CANLI — mock katmanı yok (firma sayfalarındaki bölümler demo/mock görünümde hiç çizilmiyor).
 * Rehber: FDS-Backend/guides/FE-PersonnelController-guide.md §8–§9.
 *
 * ⚠️ Hata nesnesi `status` ve `data` taşır: taşıma 409'unda staff `data.digerSahipler`i görür, bunu
 * mesajla birlikte göstermek gerekiyor ("reddi olduğu gibi göster").
 */
import axios from 'axios';
import { JAVA_BACKEND_API_URL, getAuthHeader } from '../Components/Contexts/BackendConstants';
import i18n, { currentLocaleTag } from "../i18n";

const BASE = `${JAVA_BACKEND_API_URL}/personnel`;

// ─── Belge tipleri ───────────────────────────────────────────────────────────────────

/** SGK / İSG slotlarının varyantları — `documents.sgk` / `documents.isg` tek slot, birden çok tip doldurur. */
export const BELGE_TIP = {
  SGK_ESKI: 6,
  ISG_ESKI: 7,
  SGK_ISE_GIRIS: 95,
  SGK_HIZMET_DOKUMU: 96,
  ISG_SERTIFIKA: 97,
  ISG_KATILIM_LISTESI: 98,
  HAVUZ: 92,
};

export const PAYLASIMLI_TIP_IDS = [BELGE_TIP.SGK_HIZMET_DOKUMU, BELGE_TIP.ISG_KATILIM_LISTESI];

/**
 * #503: kişi belgesi kutusunun anahtarı → kurumun o kutu için verdiği şablonun belge türü (document_template.file_type_id):
 * KVKK kutusu 56 (KVKK İmza Belgeleri), İSG kutusu 98 (İSG Eğitim Katılım Listesi). Kutunun öteki varyantları (SGK, sertifika) şablonsuz.
 */
export const BELGE_SABLON_TIPI = { kvkk: 56, isg: BELGE_TIP.ISG_KATILIM_LISTESI };

/**
 * `documents{}` anahtarı → /dosyaekle `file_type_id` (rehber §7: 8 Sağlık · 9 Mesleki Eğitim · 10 Yüksekte
 * Çalışma · 58 Aşı Kartı · 90 Kimlik Fotoğrafı). Meslek kuralının eklediği tanınmayan tür `fileType{id}`
 * anahtarıyla gelir, id anahtarın içinde. SGK / İSG (iki varyantlı: BELGE_VARYANTLARI) ve KVKK (kendi ucu:
 * uploadKvkkBelgesi) BURADA YOK — çağıran onları ayrı yönlendirir. Bilinmeyen anahtar → null (Yükle gösterilmez).
 */
const BELGE_ANAHTAR_TIP = {
  saglik: 8,
  meslekiEgitim: 9,
  yuksekteCalisma: 10,
  asiKarti: 58,
  kimlikFotografi: 90,
};
export function belgeAnahtariTipId(key) {
  if (BELGE_ANAHTAR_TIP[key]) return BELGE_ANAHTAR_TIP[key];
  const m = /^fileType(\d+)$/.exec(String(key || ''));
  return m ? Number(m[1]) : null;
}

/** Slotun iki varyantı: `tek` = bir kişinin belgesi (/dosyaekle), `paylasimli` = firmanın belgesi. */
export const BELGE_VARYANTLARI = {
  sgk: {
    slotAdi: 'SGK',
    tek: { fileTypeId: BELGE_TIP.SGK_ISE_GIRIS, get ad() { return i18n.t("services:personelBelgeService.sgkIseGirisBildirgesi"); } },
    paylasimli: { fileTypeId: BELGE_TIP.SGK_HIZMET_DOKUMU, get ad() { return i18n.t("services:personelBelgeService.sgkHizmetDokumu"); } },
  },
  isg: {
    get slotAdi() { return i18n.t("services:personelBelgeService.isg"); },
    tek: { fileTypeId: BELGE_TIP.ISG_SERTIFIKA, get ad() { return i18n.t("services:personelBelgeService.isgSertifikasi"); } },
    paylasimli: { fileTypeId: BELGE_TIP.ISG_KATILIM_LISTESI, get ad() { return i18n.t("services:personelBelgeService.isgEgitimKatilimListesi"); } },
  },
};

export const PAYLASIMLI_TIPLER = [
  { slot: 'sgk', ...BELGE_VARYANTLARI.sgk.paylasimli },
  { slot: 'isg', ...BELGE_VARYANTLARI.isg.paylasimli },
];

/** `documents[key].fileTypeId` → ekranda yazılacak varyant adı. Bilinmeyen tip için null. */
const VARYANT_ADLARI = {
  get [BELGE_TIP.SGK_ESKI]() { return i18n.t("services:personelBelgeService.sgkEskiKayit"); },
  get [BELGE_TIP.ISG_ESKI]() { return i18n.t("services:personelBelgeService.isgEgitimiEskiKayit"); },
  get [BELGE_TIP.SGK_ISE_GIRIS]() { return i18n.t("services:personelBelgeService.iseGirisBildirgesi"); },
  get [BELGE_TIP.SGK_HIZMET_DOKUMU]() { return i18n.t("services:personelBelgeService.hizmetDokumu"); },
  get [BELGE_TIP.ISG_SERTIFIKA]() { return i18n.t("services:personelBelgeService.sertifika"); },
  get [BELGE_TIP.ISG_KATILIM_LISTESI]() { return i18n.t("services:personelBelgeService.egitimKatilimListesi"); },
};
export const belgeVaryantAdi = (fileTypeId) => VARYANT_ADLARI[fileTypeId] ?? null;

/** Slot dolu belge paylaşımlı bir firma belgesi mi (96/98)? */
export const paylasimliMi = (doc) => doc?.paylasimliBelgeId != null;

/**
 * Paylaşımlı belgenin dosyası yalnızca sahibi firmaya ve personele açılır — başka bir firma kutunun
 * dolu olduğunu görür, dosyayı AÇAMAZ (403). `viewerFirmaId` null ise (personel) hep açılır.
 */
export function dosyaAcilabilir(doc, viewerFirmaId = null) {
  if (!paylasimliMi(doc)) return true;
  if (viewerFirmaId == null) return true;
  return Number(doc.paylasimliBelgeFirmaId) === Number(viewerFirmaId);
}

// ─── Hata ve ortak yardımcılar ───────────────────────────────────────────────────────

// Backend hatası bazen düz metin, bazen {message,…} zarfı (bkz. rehber "Error bodies are inconsistent").
// `status` ve `data` taşınıyor: 409 taşıma reddinde staff'a `data.digerSahipler` geliyor.
function parseError(error) {
  const body = error.response?.data;
  const message = typeof body === 'string' ? body : body?.message ?? error.message ?? i18n.t("services:personelBelgeService.bilinmeyenHata");
  const wrapped = new Error(message);
  wrapped.status = error.response?.status ?? null;
  wrapped.data = typeof body === 'object' ? body?.data ?? null : null;
  throw wrapped;
}

const unwrap = (response) => response?.data?.data ?? response?.data ?? null;

/** Kimlik doğrulamalı ikili indirir; `{ url, type }` döner (kullanınca `URL.revokeObjectURL(url)`). */
async function fetchBlob(url) {
  try {
    const response = await axios.get(url, { headers: getAuthHeader(), responseType: 'blob' });
    return { url: URL.createObjectURL(response.data), type: response.data?.type || '' };
  } catch (error) {
    let message = error.message;
    const blob = error.response?.data;
    if (blob instanceof Blob) {
      try {
        message = JSON.parse(await blob.text()).message || message;
      } catch {
        /* gövde JSON değilse mesaj olduğu gibi kalır */
      }
    }
    const wrapped = new Error(message);
    wrapped.status = error.response?.status ?? null;
    throw wrapped;
  }
}

export const BELGE_UZANTILARI = ['pdf', 'jpg', 'jpeg', 'png'];
export const BELGE_MAX_MB = 20;

/** Yükleme öncesi istemci denetimi — backend aynı kuralları 400 ile yineler. Hata metni ya da null. */
export function dosyaHatasi(file) {
  if (!file) return i18n.t("services:personelBelgeService.birDosyaSecin");
  const uzanti = String(file.name || '').split('.').pop().toLowerCase();
  if (!BELGE_UZANTILARI.includes(uzanti)) return i18n.t("services:personelBelgeService.gecersizDosyaTipiIzinVerilenler");
  if (file.size > BELGE_MAX_MB * 1024 * 1024) return i18n.t("services:personelBelgeService.dosyaCokBuyukEnFazla", { BELGE_MAX_MB });
  return null;
}

// ─── 1 · Paylaşımlı belgeler (rehber §8) ─────────────────────────────────────────────

/**
 * Firmanın paylaşımlı belgeleri, en yeni yükleme önce. `aktif`: 1 güncel (varsayılan) · 0 kaldırılmış ·
 * 'hepsi'. `fileTypeId` 96 / 98 (boşsa ikisi). Satır `kisiler[]` taşır — belgedeki GÜNCEL çalışanlar
 * (durum 0 Beklemede / 1 Onaylandı / 2 Reddedildi).
 * GET /personnel/firma/{firmaId}/paylasimli-belgeler
 */
export async function getPaylasimliBelgeler(firmaId, { aktif = 1, fileTypeId } = {}) {
  try {
    const params = { aktif };
    if (fileTypeId) params.fileTypeId = fileTypeId;
    const response = await axios.get(`${BASE}/firma/${firmaId}/paylasimli-belgeler`, {
      params,
      headers: getAuthHeader(),
    });
    return Array.isArray(response.data) ? response.data : unwrap(response) || [];
  } catch (error) {
    parseError(error);
  }
}

/**
 * Yeni paylaşımlı belge yükler. `bitisTarihi` YALNIZCA personel içindir (yyyy-MM-dd, bugün ya da sonrası — belgedeki
 * herkes için geçerli tarih; isteğe bağlı, #295): firma girişinin tarihi backend'de yok sayılır, belge tarihsiz kalır ve
 * personel onaylarken (karar) ya da `setPaylasimliBelgeGecerlilik` ile girer (#297) — bu yüzden firma çağrısı HİÇ tarih
 * göndermez. `personIds` firmanın GÜNCEL listesinden olmalı, biri dışarıdaysa 400 ve hiçbir şey
 * yazılmaz. `kaynakBelgeId`: o belgedeki mevcut çalışanları da ekler (bu ayın Hizmet Dökümünü bir
 * tıkla); sonradan ayrılanlar atlanır ve `rosterDisindaAtlananlar`da döner. Herkes Beklemede başlar.
 * POST /personnel/firma/{firmaId}/paylasimli-belgeler  (multipart)
 * @returns {{ belge, rosterDisindaAtlananlar: Array }}
 */
export async function uploadPaylasimliBelge(
  firmaId, { file, fileTypeId, bitisTarihi, ad, personIds = [], kaynakBelgeId = null },
) {
  try {
    const form = new FormData();
    form.append('file', file);
    form.append('file_type_id', String(fileTypeId));
    // WAS (until #297): form.append('file_expirydate', bitisTarihi);
    if (bitisTarihi) form.append('file_expirydate', bitisTarihi);
    if (ad && ad.trim()) form.append('file_given_name', ad.trim());
    if (personIds.length) form.append('person_ids', personIds.join(','));
    if (kaynakBelgeId) form.append('kaynak_belge_id', String(kaynakBelgeId));
    const response = await axios.post(`${BASE}/firma/${firmaId}/paylasimli-belgeler`, form, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    });
    const data = unwrap(response) || {};
    return { belge: data.belge ?? null, rosterDisindaAtlananlar: data.rosterDisindaAtlananlar ?? [] };
  } catch (error) {
    parseError(error);
  }
}

/**
 * Belgeyi daha fazla çalışana verir (hepsi ya da hiçbiri; herkes Beklemede başlar).
 * POST .../paylasimli-belgeler/{belgeId}/kisiler  { personIds }
 * @returns {{ eklenen: Array, zatenVar: Array, belge: object }}
 */
export async function addPaylasimliBelgeKisiler(firmaId, belgeId, personIds) {
  try {
    const response = await axios.post(
      `${BASE}/firma/${firmaId}/paylasimli-belgeler/${belgeId}/kisiler`,
      { personIds },
      { headers: getAuthHeader() },
    );
    const data = unwrap(response) || {};
    return { eklenen: data.eklenen ?? [], zatenVar: data.zatenVar ?? [], belge: data.belge ?? null };
  } catch (error) {
    parseError(error);
  }
}

/** Bir çalışanı belgeden çıkarır; dosya ve diğerleri kalır. DELETE .../{belgeId}/kisiler/{personId} */
export async function removePaylasimliBelgeKisi(firmaId, belgeId, personId) {
  try {
    await axios.delete(
      `${BASE}/firma/${firmaId}/paylasimli-belgeler/${belgeId}/kisiler/${personId}`,
      { headers: getAuthHeader() },
    );
  } catch (error) {
    parseError(error);
  }
}

/** Belgeyi kaldırır — üstündeki HERKES için aynı anda sayılmaz olur. DELETE .../{belgeId} */
export async function retirePaylasimliBelge(firmaId, belgeId) {
  try {
    await axios.delete(`${BASE}/firma/${firmaId}/paylasimli-belgeler/${belgeId}`, { headers: getAuthHeader() });
  } catch (error) {
    parseError(error);
  }
}

/**
 * STAFF: çalışan başına karar. `status` 1 listede · 2 listede değil (`rejectReason` ZORUNLU, ≤500) ·
 * 0 tekrar Beklemede. Birden çok çalışan tek çağrıda karara bağlanabilir ("bu 12'yi onayla"); listelenen
 * herkes belgede olmalı, yoksa hiçbir şey değişmez (400, eksikleri adıyla söyler). Firma girişi 403.
 *
 * Onayda (`status` 1) iki şey daha gerekebilir (backend #295 / #267): belgenin `gecerlilikTarihi`i (yyyy-MM-dd, bugün ya da
 * sonrası — belgede tarih yoksa ya da geçmişse ZORUNLU, 400) ve Hizmet Dökümü (96) için `belgeDonemi` ("yyyy-MM", geçen ay ya da
 * öncesi — belgede dönem yoksa ZORUNLU). İkisi de belgenin kendisine yazılır, belgedeki herkes için geçer; verilmeyen alan silinmez.
 * PATCH .../{belgeId}/karar  { personIds, status, rejectReason, gecerlilikTarihi, belgeDonemi }
 */
export async function decidePaylasimliBelge(
  firmaId, belgeId, { personIds, status, rejectReason, gecerlilikTarihi, belgeDonemi } = {},
) {
  try {
    const body = { personIds, status };
    if (status === 2) body.rejectReason = rejectReason;
    if (status === 1 && gecerlilikTarihi) body.gecerlilikTarihi = gecerlilikTarihi;
    if (status === 1 && belgeDonemi) body.belgeDonemi = belgeDonemi;
    const response = await axios.patch(
      `${BASE}/firma/${firmaId}/paylasimli-belgeler/${belgeId}/karar`,
      body,
      { headers: getAuthHeader() },
    );
    return unwrap(response);
  } catch (error) {
    parseError(error);
  }
}

/**
 * STAFF: belgenin son geçerlilik tarihini düzeltir (#297, backend #295) — belgedeki HERKES için; ilk tarih genellikle kararla
 * birlikte girer. Tarih boş bırakılamaz (silinmez), bugünden önce olamaz, kaldırılmış belgede 400; firma girişi 403.
 * PATCH .../{belgeId}/gecerlilik  { gecerlilikTarihi }  → belge satırı (liste biçimi)
 */
export async function setPaylasimliBelgeGecerlilik(firmaId, belgeId, gecerlilikTarihi) {
  try {
    const response = await axios.patch(
      `${BASE}/firma/${firmaId}/paylasimli-belgeler/${belgeId}/gecerlilik`,
      { gecerlilikTarihi },
      { headers: getAuthHeader() },
    );
    return unwrap(response)?.belge ?? null;
  } catch (error) {
    parseError(error);
  }
}

/**
 * STAFF: Hizmet Dökümü'nün (96) kapsadığı AYI girer ya da siler (#268, backend #267). `belgeDonemi` "yyyy-MM" (en son kapsanan
 * ay; geçen ay ya da öncesi — bu ay ve sonrası 400) ya da `null` (siler; belgede bir çalışan Onaylandı ise 400). 98'de 400.
 * Anahtar HEP gönderilir: `{}` silme değil hatadır.
 * PATCH .../{belgeId}/donem  { belgeDonemi }  → belge satırı (liste biçimi)
 */
export async function setPaylasimliBelgeDonem(firmaId, belgeId, belgeDonemi) {
  try {
    const response = await axios.patch(
      `${BASE}/firma/${firmaId}/paylasimli-belgeler/${belgeId}/donem`,
      { belgeDonemi: belgeDonemi || null },
      { headers: getAuthHeader() },
    );
    return unwrap(response)?.belge ?? null;
  } catch (error) {
    parseError(error);
  }
}

// ─── Paylaşımlı belgenin tarih / dönem yardımcıları (#297 / #268) ────────────────────

/** Yerel takvim günü (yyyy-MM-dd). `toISOString()` UTC günü verir ve gece yarısı civarında bir gün geri kalır. */
export function bugunYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Hizmet Dökümü için girilebilecek EN SON ay ("yyyy-MM"): bir ay ancak ertesi ayın 26'sına kadar beyan edilir, bu ay ve sonrası 400. */
export function gecenAyYm() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** "2026-08" → "Ağustos 2026" / "August 2026" (arayüz diline göre). Ay biçimi değilse olduğu gibi döner. */
export function donemYaz(donem) {
  const [y, m] = String(donem || '').split('-').map(Number);
  return y && m >= 1 && m <= 12
    ? new Date(y, m - 1, 1).toLocaleDateString(currentLocaleTag(), { month: 'long', year: 'numeric' })
    : donem;
}

/**
 * Bir paylaşımlı belgeyi (liste satırı ya da kişi kutusu: `fileTypeId`, `expiryDate`, `expired`, `belgeDonemi`) ONAYLAMAK için
 * personelden ne istenir? Tarih: belgede yoksa ya da geçmişse (firma yüklemeleri tarihsiz gelir). Dönem: yalnızca Hizmet Dökümü'nde
 * ve belgede yoksa. İkisi de gerekmiyorsa onay tek tıktır.
 */
export function onayGereksinimi(belge) {
  const gun = belge?.expiryDate ? String(belge.expiryDate).slice(0, 10) : '';
  return {
    tarihGerekli: !gun || belge?.expired === true || gun < bugunYmd(),
    donemGerekli: Number(belge?.fileTypeId) === BELGE_TIP.SGK_HIZMET_DOKUMU && !belge?.belgeDonemi,
  };
}

/**
 * Kişi kutusundaki (`documents.sgk`) onaylı bir Hizmet Dökümü'nün "eski / dönemi yok" notu — kartı ASLA engellemez (#267), kartı
 * basan kişi e-Devlet'e bakıp bakmayacağına kendisi karar verir. Not yoksa null. Aynı metinler KartUygunsuzluklari'nda `uyarilar[]`
 * kodlarından da çıkar; burada yalnızca kutunun alanlarından (kare ipucu + işaret).
 */
export function hizmetDokumuNotu(doc) {
  if (!doc || Number(doc.fileTypeId) !== BELGE_TIP.SGK_HIZMET_DOKUMU || doc.status !== 1) return null;
  if (doc.belgeDonemiGirilmedi) return i18n.t('components:kartUygunsuzluklari.sgkHizmetDokumuDonemiGirilmemis');
  if (doc.belgeDonemiEski) {
    return i18n.t('components:kartUygunsuzluklari.sgkHizmetDokumuAyEski', {
      donemYaz: donemYaz(doc.belgeDonemi), belgeDonemiAyFarki: doc.belgeDonemiAyFarki,
    });
  }
  return null;
}

/** Belgenin dosyası — yalnızca sahibi firma ve personel. GET .../{belgeId}/dosya → { url, type } */
export async function fetchPaylasimliBelgeDosya(firmaId, belgeId) {
  return fetchBlob(`${BASE}/firma/${firmaId}/paylasimli-belgeler/${belgeId}/dosya`);
}

// ─── Tek kişilik yükleme (varyant seçiminin "tek" kolu) ──────────────────────────────

/**
 * Bir kişinin tek-kişilik belgesini yükler (95 İşe Giriş Bildirgesi / 97 İSG Sertifikası …).
 * 96 / 98 burada 400 alır — onlar paylaşımlı uçtan yüklenir.
 * POST /personnel/dosyaekle/{personId} (multipart). `data` yeni file_id.
 * `expiryDate` (yyyy-MM-dd) yalnızca denetmen için anlamlı (#295); verilmezse backend bugün + 60 gün yazar.
 * `meslekId` (#372, backend #371): yalnızca Mesleki Eğitim (tip 9) — sertifikayı o mesleğe etiketler (başka tipte 400).
 * Etiketsiz kalan sertifika, kişi 2+ meslek taşıyorsa hiçbir meslek için sayılmaz.
 * #387: `fuarId` belgenin fuarıdır (`file.fuar_id`), form alanı `fuar_id` olarak gider; verilmezse backend kullanıcının
 * SEÇİLİ fuarını veritabanından okur. WAS (until #387): `X-fuar-id` başlığında gidiyordu, backend okumuyordu.
 */
export async function uploadKisiBelgesi(personId, fileTypeId, file, { fuarId, expiryDate, meslekId } = {}) {
  try {
    const form = new FormData();
    form.append('file_type_id', String(fileTypeId));
    form.append('file', file);
    if (expiryDate) form.append('file_expirydate', expiryDate);
    if (meslekId) form.append('meslek_id', String(meslekId));
    if (Number(fuarId) > 0) form.append('fuar_id', String(fuarId));   // #387
    const headers = { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' };
    // WAS (until #387): if (fuarId) headers['X-fuar-id'] = fuarId;
    const response = await axios.post(`${BASE}/dosyaekle/${personId}`, form, { headers });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * KVKK belgesini (file_type 56) yükler — backlog #137. KVKK kişinin kendi rızasıdır; backend #249'dan
 * beri firma hesabına 403 verir (hem bu uç hem /dosyaekle file_type 56), yani YALNIZCA denetmen çağırır.
 * Belge `file_expirydate` = 2030-01-01 ile yazılır; `person_kvkk_status_id` bayrağına DOKUNMAZ (iki
 * alan birbirinden bağımsız). `displayName` yalnızca `file_given_name`i kurmak için, saklanmıyor.
 * ⚠️ Doğrulama reddi (boş/geçersiz tip/20 MB) bu uçta 500 döner, 400 değil — mesaj gövdede.
 * POST /personnel/kartduzenle/{personId}/kvkk (multipart: person_display_name, kvkk_file). `data` yeni file_id.
 */
export async function uploadKvkkBelgesi(personId, file, displayName) {
  try {
    const form = new FormData();
    form.append('person_display_name', displayName || '');
    form.append('kvkk_file', file);
    const response = await axios.post(`${BASE}/kartduzenle/${personId}/kvkk`, form, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

// ─── 2 · Belge taşıma (rehber §9) ────────────────────────────────────────────────────

/**
 * Bir belgeyi başka kategoriye ve/veya aynı firmadaki başka kişiye taşır. `fileTypeId` 92 = havuza al.
 * `gecerlilikTarihi` belgenin son kullanma tarihi YOKSA (ya da havuzdan çıkıyorsa) zorunlu (400).
 * Cevap: { fileId, kaynakPersonId, hedefPersonId, eskiFileTypeId, yeniFileTypeId, gecerlilikTarihi,
 *          onaySifirlandi, havuzda } — iki kişiyi de yeniden okuyun.
 *
 * #372 (backend #371): `meslekId` bir Mesleki Eğitim sertifikasını (yeniden) etiketler — `null`/0 etiketi kaldırır,
 * VERİLMEZSE etiket korunur (bu yüzden `undefined` ile `null` farklıdır). Tek başına etiket değişikliği de bir taşımadır:
 * inceleme sıfırlanır, firmanınki Beklemede'ye döner.
 * POST /personnel/{personId}/dosya/{fileId}/tasi
 */
export async function tasiBelge(personId, fileId, { hedefPersonId, fileTypeId, gecerlilikTarihi, sebep, meslekId } = {}) {
  try {
    const body = {};
    if (hedefPersonId) body.hedefPersonId = Number(hedefPersonId);
    if (fileTypeId) body.fileTypeId = Number(fileTypeId);
    if (meslekId !== undefined) body.meslekId = meslekId == null ? null : Number(meslekId);
    if (gecerlilikTarihi) body.gecerlilikTarihi = gecerlilikTarihi;
    if (sebep && sebep.trim()) body.sebep = sebep.trim();
    const response = await axios.post(`${BASE}/${personId}/dosya/${fileId}/tasi`, body, { headers: getAuthHeader() });
    return unwrap(response);
  } catch (error) {
    parseError(error);
  }
}

/**
 * Kişiye gelen / kişiden giden her taşıma, en yeni önce — belgede küçük bir "taşındı" notu için.
 * Okuma hatası sayfayı bozmasın diye çağıran sessizce boş liste kullanır.
 * GET /personnel/{personId}/belge-tasimalari
 */
export async function getBelgeTasimalari(personId) {
  try {
    const response = await axios.get(`${BASE}/${personId}/belge-tasimalari`, { headers: getAuthHeader() });
    const data = unwrap(response);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    parseError(error);
  }
}

// ─── 3 · Kişi havuzu (rehber §9, #250) ───────────────────────────────────────────────

/** Havuzdaki dosyalar, en yeni önce: { fileId, orijinalAd, uzanti, yukleyenAdi, yuklemeTarihi, oncekiFileTypeId }. */
export async function getHavuz(personId) {
  try {
    const response = await axios.get(`${BASE}/${personId}/havuz`, { headers: getAuthHeader() });
    const data = Array.isArray(response.data) ? response.data : unwrap(response);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    parseError(error);
  }
}

/**
 * Havuza birden çok dosya yükler (en fazla 50; kısmi başarı NORMAL). Cevap kapı kuyruğuyla aynı
 * biçimde ZARFSIZ: { toplam, basariliAdet, hataliAdet, yuklenenler[], reddedilenler[{orijinalAd, hata}] }.
 * POST /personnel/{personId}/havuz  (multipart, alan adı `files`)
 */
export async function uploadHavuz(personId, files) {
  try {
    const form = new FormData();
    files.forEach((f) => form.append('files', f));
    const response = await axios.post(`${BASE}/${personId}/havuz`, form, {
      headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/**
 * Havuzdan bir ya da birden çok dosyayı siler (#269; backend #298). POST /personnel/{personId}/havuz/sil { fileIds }
 * — en fazla 50 dosya/istek, daha fazlasını çağıran parçalar. Her dosyaya AYRI karar verilir: yalnız bu kişinin
 * havuzundaki (tip 92) dosyalar silinir ve bağları arşivlenir, ötekiler `reddedilenler`de döner (200, kısmi başarı
 * normal). Cevap ZARFSIZ, havuz yüklemesiyle aynı aile:
 * { toplam, silinenAdet, hataliAdet, silinenler[fileId], reddedilenler[{ fileId, orijinalAd, hata }] }.
 * Eski `DELETE /personnel/dosyasil` artık hiçbir yerden çağrılmıyor (arşivlemiyor, tip denetlemiyor).
 */
export async function deleteHavuzDosyalari(personId, fileIds) {
  try {
    const response = await axios.post(`${BASE}/${personId}/havuz/sil`, { fileIds }, { headers: getAuthHeader() });
    return response.data;
  } catch (error) {
    parseError(error);
  }
}

/** Kişi dosyasını önizleme için çeker: { url, type }. GET /personnel/{personId}/dosya/{fileId} */
export async function fetchKisiDosyaOnizleme(personId, fileId) {
  return fetchBlob(`${BASE}/${personId}/dosya/${fileId}`);
}
